import React, { useCallback } from "react";
import { FormattedMessage } from "react-intl";
import { useSelector } from "react-redux";

import * as api from "@nens/threedi/openapi-client";

import { getV3Api } from "../../apiConfiguration";
import Duration from "../../components/Duration";
import { RAIN_RASTERS, getRainRasterByUuid } from "../../data/rasterForecastRainRasters";
import { rainEventActiveStatus } from "../../state/existingEvents";
import { getSimulationStatus } from "../../state/simulation";
import { menuExistingItemCardRow } from "../../styles/styleComposition";
import { simulationIdFromUrl } from "../../utils/apiUtils";
import menuExistingItemStyles from "../MenuExistingItemStyles.module.css";

import ExistingItemCard from "./ExistingItemCard";
import ExistingLizardRasterRainBarChart from "./ExistingLizardRasterRainBarChart";

export interface Props {
  existingLizardRasterRain: api.LizardRasterRain;
}

const ExistingLizardRasterRainCard: React.FC<Props> = ({ existingLizardRasterRain }) => {
  const simulationStatus = useSelector(getSimulationStatus);
  const rainRaster = getRainRasterByUuid(
    RAIN_RASTERS,
    existingLizardRasterRain.referenceUuid || ""
  );
  const active = rainEventActiveStatus(existingLizardRasterRain, simulationStatus.time);
  const stopDateTime = new Date(existingLizardRasterRain.startDatetime.getTime());
  stopDateTime.setSeconds(stopDateTime.getSeconds() + (existingLizardRasterRain.duration || 0));

  const onDelete = useCallback(async () => {
    const v3Api = getV3Api();
    if (
      existingLizardRasterRain &&
      existingLizardRasterRain.id &&
      existingLizardRasterRain.simulation
    ) {
      try {
        await v3Api.simulationsEventsRainRastersLizardDelete({
          id: existingLizardRasterRain.id,
          simulationPk: simulationIdFromUrl(existingLizardRasterRain.simulation),
        });
      } catch (e) {
        console.error("error deleting existing raster rain", e);
      }
    }
  }, [existingLizardRasterRain]);

  // https://github.com/nens/threedi-live-site/issues/1079
  // const simulationTime = simulationStatus.time;
  // const onStop = useCallback(async () => {
  //   const v3Api = getV3Api();
  //   if (
  //     existingLizardRasterRain &&
  //     existingLizardRasterRain.id &&
  //     existingLizardRasterRain.simulation
  //   ) {
  //     try {
  //       await v3Api.simulationsEventsRainRastersLizardPartialUpdate({
  //         id: existingLizardRasterRain.id,
  //         simulationPk: simulationIdFromUrl(existingLizardRasterRain.simulation),
  //         // Cannot patch resource, only the duration parameter might be changed after the simulation has been started
  //         // @ts-ignore
  //         data: {
  //           duration: Math.max(simulationTime + 1 - existingLizardRasterRain.offset, 1),
  //           // offset: existingLizardRasterRain.offset,
  //           // startDatetime: existingLizardRasterRain.startDatetime,
  //           // referenceUuid: existingLizardRasterRain.referenceUuid,
  //         },
  //       });
  //     } catch (e) {
  //       console.error("error stopping existing raster rain", e)
  //     }
  //   }
  // }, [existingLizardRasterRain, simulationTime]);

  return (
    <ExistingItemCard
      key={existingLizardRasterRain.url}
      onDelete={onDelete}
      // https://github.com/nens/threedi-live-site/issues/1079
      // onStop={onStop}
      start={existingLizardRasterRain.offset}
      end={
        existingLizardRasterRain.duration
          ? existingLizardRasterRain.offset + existingLizardRasterRain.duration
          : undefined
      }
      controlType="editRain"
    >
      <div className={menuExistingItemCardRow}>
        <span>Type:</span>
        <span className={menuExistingItemStyles.ItemName}>Raster</span>
      </div>
      <div className={menuExistingItemCardRow}>
        <span>
          <FormattedMessage id="existingRainMenu.start_label" defaultMessage="Start" />:
        </span>
        <span>{existingLizardRasterRain.startDatetime.toLocaleString()}</span>
      </div>
      {existingLizardRasterRain.duration ? (
        <div className={menuExistingItemCardRow}>
          <span>
            <FormattedMessage id="existingRainMenu.duration_label" defaultMessage="Duration" />:
          </span>
          <span>
            <Duration duration={existingLizardRasterRain.duration} />
          </span>
        </div>
      ) : null}
      <div className={menuExistingItemCardRow}>
        <span>
          <FormattedMessage id="existingRainMenu.status_label" defaultMessage="Status" />:
        </span>
        <span>
          {active ? (
            <FormattedMessage id="existingRainMenu.status_label_active" defaultMessage="Active" />
          ) : (
            <FormattedMessage
              id="existingRainMenu.status_label_inactive"
              defaultMessage="Inactive"
            />
          )}
        </span>
      </div>
      {rainRaster ? (
        <div className={menuExistingItemCardRow} style={{ maxHeight: "100px" }}>
          <ExistingLizardRasterRainBarChart
            existingLizardRasterRain={existingLizardRasterRain}
            rainRaster={rainRaster}
          />
        </div>
      ) : null}
    </ExistingItemCard>
  );
};

export default ExistingLizardRasterRainCard;
